import { useEffect, useRef } from "react";
import { apiGetRun } from "../../pages/predictiveApi";

/**
 * useRunPolling
 * -------------
 * Polls active / running predictive runs every `interval` ms.
 *
 * For every scenario whose slot status is "active", "queued", or "running"
 * **and** whose backend run status is NOT yet "done", it fetches the current
 * run object from the API and updates the scenario slot with the latest
 * `progress` and `status`.
 *
 * When the backend reports `status === "done"`, the slot is patched once
 * to reflect `progress: 1`, `status: "active"` (simulation results are
 * now available) and the `computingDone` flag is set so the UI can show
 * a "completed" visual instead of a progress bar.
 *
 * @param {Array}    scenarios        – full scenarios array from context
 * @param {Function} setScenarios     – setter from context
 * @param {number}   [interval=30000]  – polling interval in ms
 */
export default function useRunPolling(scenarios, setScenarios, interval = 30000) {
  const timerRef = useRef(null);
  // Keep a stable reference to avoid stale closures
  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;

  useEffect(() => {
    const poll = async () => {
      const current = scenariosRef.current;
      if (!current || current.length === 0) return;

      // Find scenarios that are computing (not yet "done" on the backend)
      const computing = current.filter((s) => {
        const st = s.status;
        const isComputing =
          st === "active" || st === "queued" || st === "running";
        // Skip if we already know computing is done
        if (s.runProgress >= 1 || s.computingDone) return false;
        return isComputing && s.lastRunId;
      });

      if (computing.length === 0) return;

      // Fetch run data for each computing scenario (in parallel)
      const updates = await Promise.allSettled(
        computing.map(async (s) => {
          const run = await apiGetRun(s.lastRunId);
          return { key: s.key, run };
        })
      );

      setScenarios((prev) => {
        let changed = false;
        const next = prev.map((s) => {
          const result = updates.find(
            (u) => u.status === "fulfilled" && u.value.key === s.key
          );
          if (!result) return s;

          const run = result.value.run;
          if (!run) return s;

          const newProgress = typeof run.progress === "number" ? run.progress : s.runProgress ?? 0;
          const isDone = run.status === "done";

          // Nothing changed — skip
          if (
            newProgress === (s.runProgress ?? 0) &&
            isDone === (s.computingDone ?? false)
          ) {
            return s;
          }

          changed = true;
          return {
            ...s,
            runProgress: newProgress,
            computingDone: isDone,
            // Keep scenarios "active" status; don't downgrade to "idle"
            status: isDone ? "active" : s.status,
            scenario: {
              ...s.scenario,
              is_active: isDone ? true : s.scenario?.is_active,
            },
          };
        });

        return changed ? next : prev;
      });
    };

    // Initial poll immediately
    poll();

    timerRef.current = setInterval(poll, interval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [setScenarios, interval]);
}
